import { Box, IconButton, Button, Input, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../AuthContext";
import { tokens } from "../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Container, Form } from "react-bootstrap";
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AddIcon from '@mui/icons-material/Add';
import 'react-toastify/dist/ReactToastify.css';
import Nodata from '../../assets/errors/nodata.png';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import DownloadIcon from '@mui/icons-material/Download';

const Leave = () => {
  const [leaves, setLeaves] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [leaveToDelete, setLeaveToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData, userID, userRole } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    staff_id: '',
    leave_type: '',
    reason: '',
    startDate: '',
    endDate: '',
    back_in_office: '',
    department: '',
    hr_appr_id: '',
    hr_appr_date: '',
    suppervisor_appr_id: '',
    suppervisor_appr_date: '',
    status: '0', // Default to pending
    check_in_date: '',
    approved_by: '',
    rejected_date: '',
    rejected_by: '',
    comments: '',
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const [leavesResponse, usersResponse] = await Promise.all([
          getData('proccess.php', 'leaves_V2'),
          getData('proccess.php', 'staffs')
        ]);

        if (leavesResponse.success) {
          setLeaves(leavesResponse.data);
        } else {
          toast.error(leavesResponse.message || "Failed to load leaves");
        }

        if (usersResponse.success) {
          setStaffs(usersResponse.data);
        } else {
          toast.error(usersResponse.message || "Failed to load staff");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch data");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Function to export data to Excel
  const exportToExcel = () => {
    if (leaves.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      // Map status codes to readable text
      const statusMap = {
        0: 'Pending',
        1: 'Inprogress',
        2: 'Approved',
        3: 'Rejected',
        4: 'Returned'
      };

      // Prepare data for export
      const exportData = leaves.map(leave => {
        const staff = staffs.find(s => s.Id === leave.staff_id);
        const hrApprover = staffs.find(s => s.Id === leave.hr_appr_id);
        const supervisorApprover = staffs.find(s => s.Id === leave.supervisor_appr_id);
        const approvedBy = staffs.find(s => s.Id === leave.approved_by);
        const rejectedBy = staffs.find(s => s.Id === leave.rejected_by);

        return {
          'ID': leave.id || '',
          'Staff Name': staff ? `${staff.FirstName} ${staff.LastName}` : 'Unknown',
          'Leave Type': leave.leave_type || '',
          'Reason': leave.reason || '',
          'Start Date': leave.startDate || '',
          'End Date': leave.endDate || '',
          'Back In Office': leave.back_in_office || '',
          'Department': leave.department || '',
          'HR Approver': hrApprover ? `${hrApprover.FirstName} ${hrApprover.LastName}` : '',
          'HR Approval Date': leave.hr_appr_date || '',
          'Supervisor Approver': supervisorApprover ? `${supervisorApprover.FirstName} ${supervisorApprover.LastName}` : '',
          'Supervisor Approval Date': leave.supervisor_appr_date || '',
          'Status': statusMap[leave.status] || 'Unknown',
          'Check In Date': leave.check_in_date || '',
          'Approved By': approvedBy ? `${approvedBy.FirstName} ${approvedBy.LastName}` : 'N/A',
          'Rejected Date': leave.rejected_date || '',
          'Rejected By': rejectedBy ? `${rejectedBy.FirstName} ${rejectedBy.LastName}` : 'N/A',
          'Comments': leave.comments || ''
        };
      });

      // Create CSV content
      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => 
          headers.map(header => {
            const value = row[header] || '';
            // Escape quotes and wrap in quotes if contains comma
            return `"${String(value).replace(/"/g, '""')}"`;
          }).join(',')
        )
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `leaves_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success("Data exported successfully");
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export data");
    }
  };

  // Alternative function using XLSX library (if you prefer proper Excel format)
  // You'll need to install: npm install xlsx
  /*
  const exportToExcelAdvanced = async () => {
    if (leaves.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const XLSX = await import('xlsx');
      
      // Prepare data (same as above)
      const statusMap = {
        0: 'Pending',
        1: 'Inprogress',
        2: 'Approved',
        3: 'Rejected',
        4: 'Returned'
      };

      const exportData = leaves.map(leave => {
        const staff = staffs.find(s => s.Id === leave.staff_id);
        const hrApprover = staffs.find(s => s.Id === leave.hr_appr_id);
        const supervisorApprover = staffs.find(s => s.Id === leave.supervisor_appr_id);
        const approvedBy = staffs.find(s => s.Id === leave.approved_by);
        const rejectedBy = staffs.find(s => s.Id === leave.rejected_by);

        return {
          'ID': leave.id || '',
          'Staff Name': staff ? `${staff.FirstName} ${staff.LastName}` : 'Unknown',
          'Leave Type': leave.leave_type || '',
          'Reason': leave.reason || '',
          'Start Date': leave.startDate || '',
          'End Date': leave.endDate || '',
          'Back In Office': leave.back_in_office || '',
          'Department': leave.department || '',
          'HR Approver': hrApprover ? `${hrApprover.FirstName} ${hrApprover.LastName}` : '',
          'HR Approval Date': leave.hr_appr_date || '',
          'Supervisor Approver': supervisorApprover ? `${supervisorApprover.FirstName} ${supervisorApprover.LastName}` : '',
          'Supervisor Approval Date': leave.supervisor_appr_date || '',
          'Status': statusMap[leave.status] || 'Unknown',
          'Check In Date': leave.check_in_date || '',
          'Approved By': approvedBy ? `${approvedBy.FirstName} ${approvedBy.LastName}` : 'N/A',
          'Rejected Date': leave.rejected_date || '',
          'Rejected By': rejectedBy ? `${rejectedBy.FirstName} ${rejectedBy.LastName}` : 'N/A',
          'Comments': leave.comments || ''
        };
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Leaves Data');
      
      // Generate Excel file and download
      XLSX.writeFile(wb, `leaves_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      toast.success("Data exported successfully as Excel file");
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to export data");
    }
  };
  */

  const handleAddClick = () => {
    setFormData({
      staff_id: '',
      leave_type: '',
      reason: '',
      startDate: '',
      endDate: '',
      back_in_office: '',
      department: '',
      hr_appr_id: '',
      hr_appr_date: '',
      supervisor_appr_id: '',
      supervisor_appr_date: '',
      status: '0', // Default to pending
      check_in_date: '',
      approved_by: '',
      rejected_date: '',
      rejected_by: '',
      comments: '',
    });
    setOpenAddDialog(true);
  };

  const handleEditClick = (leave) => {
    setSelectedLeave(leave);
    setFormData({
      staff_id: leave.staff_id || '',
      leave_type: leave.leave_type || '',
      reason: leave.reason || '',
      startDate: leave.startDate || '',
      endDate: leave.endDate || '',
      back_in_office: leave.back_in_office || '',
      department: leave.department || '',
      comments: leave.comments || '',
    });
    setOpenEditDialog(true);
  };

  const handleHrApproveClick = async (leave) => {
    try {
        // Determine status and approval fields based on user role
        const response = await updateData('proccess.php', 'leaves_V2', {
          updType: 'hr',
          status: 1,
          hr_appr_id: userID, // Replace with actual user ID from auth
          hr_appr_date: new Date().toISOString().split('T')[0]
        }, leave.id);
        
        if (response.success) {
            toast.success("Leave approved successfully");
            const processedLeaves = response.data.map(leave => ({
                ...leave,
                id: leave.id
            }));
            setLeaves(processedLeaves);
        } else {
            toast.error(response.message || "Failed to approve leave");
        }
    } catch (err) {
        toast.error("Network error: Failed to approve leave");
        console.error("Approve error:", err);
    }
  };

  const handleSupervisorApproveClick = async (leave) => {
    try {
        // Determine status and approval fields based on user role
        const response = await updateData('proccess.php', 'leaves_V2', {
          updType: 'spvsr',
          status: 2,
          supervisor_appr_id: userID, // Replace with actual user ID from auth
          supervisor_appr_date: new Date().toISOString().split('T')[0]
        }, leave.id);

        
        if (response.success) {
            toast.success("Leave approved successfully");
            const processedLeaves = response.data.map(leave => ({
                ...leave,
                id: leave.id
            }));
            setLeaves(processedLeaves);
        } else {
            toast.error(response.message || "Failed to approve leave");
        }
    } catch (err) {
        toast.error("Network error: Failed to approve leave");
        console.error("Approve error:", err);
    }
  };

  const handleApproveClick = async (leave) => {
    try {
        // Determine status and approval fields based on user role
        const response = await updateData('proccess.php', 'leaves_V2', {
          updType: 'appr',
          status: 4,
          approved_by: userID, // Replace with actual user ID from auth
          check_in_date: new Date().toISOString().split('T')[0]
        }, leave.id);
        
        
        if (response.success) {
            toast.success("Leave approved successfully");
            const processedLeaves = response.data.map(leave => ({
                ...leave,
                id: leave.id
            }));
            setLeaves(processedLeaves);
        } else {
            toast.error(response.message || "Failed to approve leave");
        }
    } catch (err) {
        toast.error("Network error: Failed to approve leave");
        console.error("Approve error:", err);
    }
  };

  const handleRejectClick = async (leave) => {
    try {
      const response = await updateData('proccess.php', 'leaves_V2', {
        updType: 'reject',
        status: 3,
        rejected_by: userID, // Replace with actual user ID from auth
        rejected_date: new Date().toISOString().split('T')[0]
      }, leave.id);
      
      if (response.success) {
          toast.success("Leave rejected successfully");
          // Ensure each leave has an id
          const processedLeaves = response.data.map(leave => ({
            ...leave,
            id: leave.id // Make sure this matches your actual ID field
          }));
          setLeaves(processedLeaves);
      } else {
        toast.error(response.message || "Failed to reject leave");
      }
    } catch (err) {
      toast.error("Network error: Failed to reject leave");
      console.error("Reject error:", err);
    }
  };

  const handleDeleteClick = (leave) => {
    setLeaveToDelete(leave);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await deleteData('proccess.php', 'leaves_V2', leaveToDelete.id);
      if (response.success) {
        toast.success("Leave deleted successfully");
        setLeaves(leaves.filter(l => l.id !== leaveToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete leave");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete leave");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setLeaveToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (selectedLeave) {
        // Update existing leave
        response = await updateData('proccess.php', 'leaves_V2', formData, selectedLeave.id);
        if (response.success) {
          toast.success("Leave updated successfully");
          // Ensure each leave has an id
          const processedLeaves = response.data.map(leave => ({
            ...leave,
            id: leave.id // Make sure this matches your actual ID field
          }));
          setLeaves(processedLeaves);
        }
      } else {
        // Add new leave
        response = await addData('proccess.php', 'leaves_V2', formData);
        if (response.success) {
          toast.success("Leave added successfully");
          // Ensure each leave has an id
          const processedLeaves = response.data.map(leave => ({
            ...leave,
            id: leave.id // Make sure this matches your actual ID field
          }));
          setLeaves(processedLeaves);
        }
      }
      
      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedLeave(null);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 50 },
    {
      field: "staff_id", 
      headerName: "Full Name", 
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const staff = staffs.find(s => s.Id === params.row?.staff_id);
        return (
          <Box width="100%">
            {staff ? `${staff.FirstName} ${staff.LastName}` : 'Unknown'}
          </Box>
        );
      }
    },
    { field: "leave_type", headerName: "Leave Type", flex: 1, minWidth: 120 },
    { field: "reason", headerName: "Reason", flex: 1, minWidth: 120 },
    { 
      field: "startDate", 
      headerName: "Start Date", 
      flex: 1, 
      minWidth: 120,
    },
    { 
      field: "endDate", 
      headerName: "End Date", 
      flex: 1, 
      minWidth: 120,
    },
    { 
      field: "back_in_office", 
      headerName: "Back In Office", 
      flex: 1, 
      minWidth: 120,
    },
    { 
      field: "department", 
      headerName: "Department", 
      flex: 1, 
      minWidth: 120,
    },
    {
      field: "hr_appr_id", 
      headerName: "Hr Approval", 
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const staff = staffs.find(s => s.Id === params.row?.hr_appr_id);
        return (
          <Box width="100%">
            {staff ? `${staff.FirstName} ${staff.LastName}` : ''}
            {(userRole === 'admin' || userRole === 'hr' || userRole === 'ceo' || userRole === 'coo') && params.row?.status === 0 &&
              <IconButton 
                onClick={() => handleHrApproveClick(params.row)}
                sx={{ color: colors.blue[400] }}
                disabled={params.row?.status !== 0}
              >
                <CheckOutlinedIcon />
              </IconButton>
            }
          </Box>
        );
      }
    },
    { 
      field: "hr_appr_date", 
      headerName: "Approval Date", 
      flex: 1, 
      minWidth: 120,
    },
    {
      field: "supervisor_appr_id", 
      headerName: "Supervisor Approval", 
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const staff = staffs.find(s => s.Id === params.row?.supervisor_appr_id);
        return (
          <Box width="100%">
            {staff ? `${staff.FirstName} ${staff.LastName}` : ''}
            {(userRole === 'supervisor' || userRole === 'manager' || userRole === 'ceo' || userRole === 'coo') && params.row?.status === 1 &&
              <IconButton 
                onClick={() => handleSupervisorApproveClick(params.row)}
                sx={{ color: colors.blue[400] }}
                disabled={params.row?.status !== 1}
              >
                <CheckOutlinedIcon />
              </IconButton>
            }
          </Box>
        );
      }
    },
    { 
      field: "supervisor_appr_date", 
      headerName: "Approval Date", 
      flex: 1, 
      minWidth: 120,
    },
    { 
      field: "status", 
      headerName: "Status", 
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const statusText = {
          0: 'Pending',
          1: 'Inprogress',
          2: 'Approved',
          3: 'Rejected',
          4: 'Returned'
        };
        const statusColor = {
          0: colors.orange[100],
          1: colors.orange[100],
          2: colors.blue[100],
          3: colors.red[100],
          4: colors.blue[100],
        };
        return (
          <Box width={"100%"} height={"100%"} display="flex" justifyContent="center" alignItems="center">
            <Typography variant="h5" fontWeight={"bold"} color={statusColor[params.row?.status]}>
              {statusText[params.row?.status] || 'Unknown'}
            </Typography>
          </Box>
          
        );
      }
    },
    { 
      field: "check_in_date", 
      headerName: "Check In Date", 
      flex: 1, 
      minWidth: 120,
    },
    { 
      field: "approved_by", 
      headerName: "Approved By", 
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const staff = staffs.find(s => s.Id === params.row?.approved_by);
        return staff ? `${staff.FirstName} ${staff.LastName}` : 'N/A';
      }
    },
    { 
      field: "rejected_date", 
      headerName: "Rejected Date", 
      flex: 1, 
      minWidth: 120,
    },
    { 
      field: "rejected_by", 
      headerName: "Rejected By", 
      flex: 1,
      minWidth: 120,
      renderCell: (params) => {
        const staff = staffs.find(s => s.Id === params.row?.rejected_by);
        return staff ? `${staff.FirstName} ${staff.LastName}` : 'N/A';
      }
    },
    { field: "comments", headerName: "Comment", flex: 1, minWidth: 120 },
    
    {
      field: "actions",
      headerName: "Actions",
      flex: 1, 
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" justifyContent={'center'} alignItems={'center'} height={'100%'}>
          <IconButton 
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton 
            onClick={() => handleApproveClick(params.row)}
            sx={{ color: colors.blue[400] }}
            disabled={(params.row?.status === 4 || params.row?.status < 2) && (userRole === 'admin' || userRole === 'hr' || userRole === 'supervisor')}
          >
            <CheckOutlinedIcon />
          </IconButton>
          <IconButton 
            onClick={() => handleRejectClick(params.row)}
            sx={{ color: colors.red[400] }}
            disabled={(params.row?.status === 1 || params.row?.status > 2 ) && (userRole === 'admin' || userRole === 'hr' || userRole === 'supervisor')}
          >
            <ClearOutlinedIcon />
          </IconButton>
          <IconButton 
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.red[800] }}
            disabled={(params.row?.status === 1 || params.row?.status > 1) && (userRole === 'admin' || userRole === 'hr' || userRole === 'supervisor')}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img 
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No data available</Typography>
      </Box>
    );
  };

  return (
    <Box m={"10px"}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      {/* Add/Edit Dialog */}
      <Dialog 
        open={openEditDialog || openAddDialog} 
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedLeave(null);
        }} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>{selectedLeave ? "Edit Leave" : "Add New Leave"}</DialogTitle>
        <DialogContent>
          <Form onSubmit={handleSubmit} style={{width: "100%", padding: "1rem"}}>
            <Box display={"flex"} gap={"1rem"} width={"100%"} mb={"1rem"} flexWrap="wrap">
              <TextField
                select
                style={{width: "100%"}}
                id="staff_id"
                name="staff_id"
                label="Staff Member"
                variant="standard"
                value={formData.staff_id}
                onChange={handleFormChange}
                required
                SelectProps={{
                  native: true,
                }}
              >
                <option value=""></option>
                {staffs.map((staff) => (
                  <option key={staff.Id} value={staff.Id}>
                    {staff.FirstName} {staff.LastName}
                  </option>
                ))}
              </TextField>

              <TextField
                select
                style={{width: "100%"}}
                id="leave_type"
                name="leave_type"
                label="Leave Type"
                variant="standard"
                value={formData.leave_type}
                onChange={handleFormChange}
                required
                SelectProps={{
                  native: true,
                }}
              >
                <option value=""></option>
                <option key='Annual' value='Annual'>Annual</option>
                <option key='Sick' value='Sick'>Sick</option>
                <option key='Compassionate' value='Compassionate'>Compassionate</option>
                <option key='Study' value='Study'>Study</option>
                <option key='Maternity' value='Maternity'>Maternity</option>
                
              </TextField>
              
              <TextField 
                style={{width: "100%"}} 
                id="reason" 
                name="reason"
                label="Reason" 
                variant="standard" 
                value={formData.reason}
                onChange={handleFormChange}
                required
                multiline
                rows={2}
              />
              
              <TextField
                style={{width: "48%"}}
                id="startDate"
                name="startDate"
                label="Start Date"
                type="date"
                variant="standard"
                value={formData.startDate}
                onChange={handleFormChange}
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />
              
              <TextField
                style={{width: "48%"}}
                id="endDate"
                name="endDate"
                label="End Date"
                type="date"
                variant="standard"
                value={formData.endDate}
                onChange={handleFormChange}
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                style={{width: "48%"}}
                id="back_in_office"
                name="back_in_office"
                label="Back In Office"
                type="date"
                variant="standard"
                value={formData.back_in_office}
                onChange={handleFormChange}
                required
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                select
                style={{width: "48%"}}
                id="department"
                name="department"
                label="Department"
                variant="standard"
                value={formData.department}
                onChange={handleFormChange}
                required
                SelectProps={{
                  native: true,
                }}
              >
                <option value=""></option>
                <option key='ICT' value='ICT'>ICT</option>
                <option key='SCIENCE' value='SCIENCE'>SCIENCE</option>
                <option key='ART' value='ART'>ART</option>
                <option key='HR' value='HR'>HR</option>
                <option key='ADMIN' value='ADMIN'>ADMIN</option>
                <option key='STEAM' value='STEAM'>STEAM</option>
                <option key='ENGINEERING' value='ENGINEERING'>ENGINEERING</option>
              </TextField>
              
              {selectedLeave && (
                <>
                  <TextField
                    style={{width: "100%"}}
                    id="comments"
                    name="comments"
                    label="Comments"
                    variant="standard"
                    value={formData.comments}
                    onChange={handleFormChange}
                    multiline
                    rows={2}
                  />
                </>
              )}
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenEditDialog(false);
            setOpenAddDialog(false);
          }}>Cancel</Button>
          <Button 
            onClick={handleSubmit}
            sx={{backgroundColor: colors.blue[400]}} 
            variant="contained" 
            endIcon={<SendIcon />}
          >
            {selectedLeave ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this leave request?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button 
            onClick={confirmDelete}
            sx={{backgroundColor: colors.red[400]}} 
            variant="contained"
            startIcon={<DeleteForeverOutlinedIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mb={"30px"} ml={"15px"} mr={"15px"} display={"flex"} alignItems={"center"} justifyContent= {"space-between"}>
        <Box flex={1}>
          <Typography
            variant="h4"
            color={colors.dark[400]}
            fontWeight={"bold"}
            sx={{mb: "5px"}}
          >
            Staff Leaves Management
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportToExcel}
            sx={{
              borderColor: colors.green[400],
              color: colors.green[400],
              '&:hover': {
                backgroundColor: colors.green[50],
                borderColor: colors.green[500],
              }
            }}
          >
            Export Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            sx={{
              backgroundColor: colors.blue[400],
              '&:hover': {
                backgroundColor: colors.blue[500],
              }
            }}
          >
            Add Leave Request
          </Button>
        </Box>
      </Box>
      
      <Divider component="" />
      
      <Box sx={{ height: 'calc(100vh - 200px)', width: "100%" }}>
        <DataGrid
          rows={leaves || []}
          columns={columns}
          getRowId={(row) => row.id || Math.random().toString(36).substr(2, 9)}
          loading={loading}
          getRowClassName={(params) => {
            // Apply different classes based on status
            switch (params.row.status) {
              case 0: // Pending
                return 'status-pending';
              case 1: // Approved by HR/Admin
                return 'status-approved';
              case 2: // Approved by Supervisor
                return 'status-supervisor-approved';
              case 3: // Rejected
                return 'status-rejected';
              case 4: // Rejected
                return 'status-returned';
              default:
                return '';
            }
          }}
          sx={{
            border: "none",
            backgroundColor: colors.primary[500],
            // Remove individual cell styling and move to global styles
            ".MuiDataGrid-cell": {
              borderBottom: "none",
            },
            // Define your status classes
            "& .status-pending .MuiDataGrid-cell": {
              backgroundColor: '#af928257', // Light orange for pending
              color: colors.orange[200], // Dark orange text for pending
              borderBottom: `.2px solid ${colors.orange[400]}`, // Orange left border for pending
            },
            "& .status-approved .MuiDataGrid-cell": {
              backgroundColor: '#6f968459', // Light green for approved
              color: colors.newTeal[200], // Dark green text for approved
              borderBottom: `.2px solid ${colors.green[400]}`, // Green left border for approved
            },
            "& .status-supervisor-approved .MuiDataGrid-cell": {
              backgroundColor: '#60a3d356', // Light blue for supervisor approved
              color: colors.indigo[200], // Dark blue text for supervisor approved
              borderBottom: `.2px solid ${colors.blue[400]}`, // Blue left border
            },
            "& .status-rejected .MuiDataGrid-cell": {
              backgroundColor: '#cf04223a', // Light red for rejected
              color: colors.red[100], // Dark red text for rejected
              borderBottom: `.2px solid ${colors.red[400]}`, // Red left border
            },
            "& .status-returned .MuiDataGrid-cell": {
              borderBottom: `.2px solid ${colors.indigo[400]}`, // Red left border
            },
            ".MuiDataGrid-toolbarContainer": {
              padding: "10px",
              backgroundColor: colors.primary[900],
              borderBottom: "none",
            },
            ".MuiDataGrid-quickFilter": {
              padding: "8px",
              borderRadius: "4px",
              border: `1px solid ${colors.primary[100]}`,
            },
            ".MuiDataGrid-footerContainer": {
              backgroundColor: 'transparent',
            },
          }}
          slots={{
            toolbar: () => (
              <div>
                <GridToolbarQuickFilter />
              </div>
            ),
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
                placeholder: "Search...",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Leave;